import React, {useContext, useEffect, useState, useRef} from 'react';
import JSONDigger from 'json-digger';
import {v4 as uuidv4} from 'uuid';
import {cloneDeep, flattenDeep} from 'lodash';
import {StepStatus, useAppContext} from '../../../context/app-context';
import {useCompanyContext} from '../../../context/company-context';
import {useAuthContext, useClient} from '../../../context/auth-context';
import Form from '../components/Form';

export const InitialData = {
  id: 'root',
  countryCode: 'TWN',
  countyName: null,
  departmentCodePrefix: 'D',
  departmentCodeSeqNo: '001',
  departmentName: '',
  employees: [],
  supervisors: [],
  children: [
    {
      id: uuidv4(),
      countryCode: 'TWN',
      countyName: null,
      departmentCodePrefix: 'D',
      departmentCodeSeqNo: '001',
      departmentName: '',
      employees: [],
      identity: 'root',
      supervisors: [],
      error: true, // 無主管與上層主管，視為錯誤
    },
  ],
};

export const ChartDataContext = React.createContext();

export const useChartDataContext = () => {
  const context = useContext(ChartDataContext);
  if (context === undefined) {
    throw new Error(
      'useChartDataContext must be used within a ChartDataProvider',
    );
  }
  return context;
};

export const ChartDataProvider = ({children}) => {
  const api = useClient();
  const {companyId} = useAuthContext();
  const {edit, init, data, stepStatus, setIsLoading} = useAppContext();
  const {employee} = useCompanyContext();
  const selectedEmployee = useRef([]);
  const [errorMessages, setErrorMessages] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isEditRootNode, setIsEditRootNode] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const isEdit = formStatus === 'EDIT';
  const dsDigger = new JSONDigger(data, 'id', 'children');

  useEffect(() => {
    if (stepStatus === StepStatus.init) {
      setIsLoading(true);
      api
        .get(`organization/company/${companyId}`)
        .then(
          res => {
            const initData = cloneDeep(InitialData);
            if (res.organizations.length > 0) {
              initData.children = res.organizations;
            }

            initData.children = initData.children.map(node =>
              updateEmployeeData(cloneDeep({...node, identity: 'root'})),
            );
            init(initData);
          },
          () => {
            init(cloneDeep(InitialData));
          },
        )
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      updateSelectedEmployee();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onFormShow = (params = {}) => {
    const {formStatus = null, nodeData = null, isRootNode = false} = params;
    setSelectedNode(nodeData);
    setFormStatus(formStatus);
    setIsEditRootNode(isRootNode);
  };

  const onFormSubmit = submitData => {
    if (isEdit) {
      editNode({
        ...submitData,
        departmentCodeSeqNo: paddingNumber(submitData.departmentCodeSeqNo),
      });
    }
    onFormShow();
  };

  const updateEmployeeData = node => {
    const isEmployeeExist = e => employee.some(_e => e.jobNo === _e.jobNo);
    const isEmployeeHasSupervisor = e => e.supervisor;
    // 初始化同步主管資料
    if (node.supervisors.length > 0) {
      const {error, supervisors} = initSupervisorData(node);
      node.supervisors = supervisors;
      node.error = error;
    } else {
      node.supervisors = [];
      node.error = true;
    }
    node.employees = node.employees
      .filter(isEmployeeExist)
      .filter(isEmployeeHasSupervisor);
    if (node.children) {
      node.children = node.children.map(updateEmployeeData);
    } else {
      // 如children為null，會刪除失敗，故移除
      delete node.children;
    }
    return node;
  };

  const initSupervisorData = node => {
    let error = false;
    const supervisors =
      node.supervisors?.map((s, idx) => {
        const supervisorData = employee.find(e => e.jobNo === s.jobNo);
        const positionOrder = `0${idx}`;
        const isSupervisorEmpty =
          !supervisorData || (node.identity !== 'root' && !s.upperSupervisor);
        if (isSupervisorEmpty) {
          // 無主管與上層主管，視為錯誤
          error = true;
        }
        if (supervisorData) {
          return {...s, ...supervisorData, positionOrder};
        }
        return {...s, positionOrder};
      }) || [];

    return {error, supervisors};
  };
  const checkNodeError = node => {
    // 無主管與上層主管，視為錯誤
    if (node.children) {
      return Boolean(node.error) || node.children.some(checkNodeError);
    }
    return Boolean(node.error);
  };

  const getSelectedEmployee = node => {
    if (node.children && node.employees) {
      node.children = node.children.map(getSelectedEmployee);
      return [...node.employees, ...node.children];
    }
    return node.employees;
  };
  const updateSelectedEmployee = async newData => {
    const _data = cloneDeep(newData || data);
    const _selectedEmployee = getSelectedEmployee(_data);
    selectedEmployee.current = flattenDeep(_selectedEmployee);
  };

  const getUnselectedEmployee = () => {
    const isSelected = jobNo =>
      selectedEmployee.current.some(s => s.jobNo === jobNo);
    return employee.filter(e => !isSelected(e.jobNo));
  };

  const getAllSeqNoByPrefix = (node, prefix) => {
    let parentSeqNo =
      node.departmentCodePrefix === prefix ? node.departmentCodeSeqNo : '';
    if (node.children) {
      node.children = node.children.map(n => getAllSeqNoByPrefix(n, prefix));
      return [parentSeqNo, ...node.children];
    }
    return [parentSeqNo];
  };

  const generateSeqNo = prefix => {
    const rootSeqNumber = data.departmentCodeSeqNo;
    let newNumber = Number(rootSeqNumber) + 1;
    if (data.children) {
      const _data = cloneDeep(data);
      const existDeptCodes = flattenDeep(getAllSeqNoByPrefix(_data, prefix));
      const lastNumberString =
        existDeptCodes.sort().reverse()[0] || String(rootSeqNumber);
      newNumber = Number(lastNumberString) + 1;
    }
    return paddingNumber(newNumber);
  };

  const checkDeptCodeRepeat = (prefix, seqNo) => {
    if (data?.children) {
      const _data = cloneDeep(data);
      const existDeptCodes = flattenDeep(getAllSeqNoByPrefix(_data, prefix));
      return existDeptCodes.some(c => c === seqNo);
    }
    return false;
  };
  const paddingNumber = number => {
    let paddingCount = 3 - String(number).length;
    if (paddingCount < 0) {
      paddingCount = 0;
    }
    let newNumber = number;
    Array(paddingCount)
      .fill()
      .forEach(() => (newNumber = '0' + newNumber));
    return newNumber;
  };

  const getInitialNodeData = (parentNode, type = 'addNode') => {
    const initNodeData = {
      id: uuidv4(),
      countryCode: parentNode.countryCode,
      countyName: parentNode.countyName,
      departmentCodePrefix: parentNode.departmentCodePrefix,
      departmentCodeSeqNo: generateSeqNo(parentNode.departmentCodePrefix),
      departmentName: '',
      employees: [],
      supervisors: [],
      identity: type === 'addSibling' ? 'root' : null,
      error: true, // 無主管與上層主管，視為錯誤
    };
    return initNodeData;
  };

  const addNode = async parentNode => {
    await dsDigger.addChildren(parentNode.id, [getInitialNodeData(parentNode)]);
    const updatedData = {...dsDigger.ds};
    edit(updatedData);

    if (isConfirmed) {
      validate(updatedData);
    }
  };

  const editNode = async nodeData => {
    const _nodeData = {
      ...nodeData,
      supervisors: nodeData.supervisors.map((s, idx) => {
        const positionOrder = `0${idx}`;
        return {...s, positionOrder};
      }),
      error: false,
    }; // 編輯成功，移除錯誤
    await checkChildUpperSupervisorIsExist(_nodeData);
    await dsDigger.updateNodes([nodeData.id], _nodeData);

    const updatedData = {...dsDigger.ds};
    edit(updatedData);

    if (isConfirmed) {
      await updateSelectedEmployee(updatedData);
      validate(updatedData);
    }
  };

  const dragNode = async (data, dragItem) => {
    await checkParentSupervisorIsExist(dragItem);
    if (dragItem.identity === 'root') {
      await dsDigger.updateNodes([dragItem.id], {...dragItem, identity: null});
    }

    const updatedData = {...data};
    edit(updatedData);

    if (isConfirmed) {
      validate(updatedData);
    }
  };

  // 檢查子節點上層單位主管是否存在，否則清空並更新
  const checkChildUpperSupervisorIsExist = async nodeData => {
    const thisNode = await getNode(nodeData.id);
    thisNode?.children?.map(async n => {
      return await checkParentSupervisorIsExist(n, nodeData);
    });
  };

  //拖移結點後，檢查該部門上層主管是否存在，否則清空並更新
  const checkParentSupervisorIsExist = async (childNode, parentNode) => {
    const _parentNode = parentNode || (await getParentNode(childNode.id));
    let isNodeNeedUpdate = false;
    let error = false;
    childNode.supervisors = childNode.supervisors.map(s => {
      if (s.supervisorStatus === 1) return s; // 次要主管不檢查
      const isUpperSupervisorNotExist = _parentNode.supervisors.every(
        ns => ns.jobNo !== s.upperSupervisor,
      );
      if (isUpperSupervisorNotExist) {
        isNodeNeedUpdate = true;
        error = 'isUpperSupervisorNotExist';
        return {...s, upperSupervisor: null}; // 無上層主管資料，視為錯誤
      }
      return s;
    });

    if (isNodeNeedUpdate) {
      await dsDigger.updateNodes([childNode.id], {...childNode, error});
    }
    return {...childNode, error};
  };

  const addSiblingNodes = async nodeData => {
    await dsDigger.addSiblings(nodeData.id, [
      getInitialNodeData(nodeData, 'addSibling'),
    ]);
    const updatedData = {...dsDigger.ds};
    edit(updatedData);

    if (isConfirmed) {
      validate(updatedData);
    }
  };

  const removeNode = async id => {
    await dsDigger.removeNodes([id]);
    const updatedData = {...dsDigger.ds};
    edit(updatedData);

    if (isConfirmed) {
      validate(updatedData);
    }
  };

  const resetChart = () => {
    const initData = cloneDeep(InitialData);
    const updatedData = {...initData};
    edit(updatedData);

    if (isConfirmed) {
      setIsConfirmed(false); // 關閉驗證
      setErrorMessages([]);
    }
  };

  const getNode = async id => {
    const tempDigger = new JSONDigger(data, 'id', 'children');
    const node = await tempDigger.findNodeById(id);
    return node;
  };
  const getParentNode = async id => {
    const tempDigger = new JSONDigger(data, 'id', 'children');
    const node = await tempDigger.findParent(id);
    return node;
  };

  const validate = data => {
    const hasUnselectedEmployee = getUnselectedEmployee().length > 0;
    const hasNodeError = data.children.some(checkNodeError);
    if (hasUnselectedEmployee || hasNodeError) {
      setErrorMessages(
        [
          hasNodeError && '部門資料尚未填寫完成',
          hasUnselectedEmployee && '尚有員工未被安排進部門中',
        ].filter(v => v),
      );
    } else {
      setErrorMessages([]);
    }
    return hasUnselectedEmployee || hasNodeError;
  };

  return (
    <ChartDataContext.Provider
      value={{
        dataSource: data,
        addNode,
        addSiblingNodes,
        editNode,
        dragNode,
        removeNode,
        resetChart,
        setSelectedNode,
        onFormShow,
        getUnselectedEmployee,
        checkNodeError,
        setIsConfirmed,
        isConfirmed,
        validate,
        errorMessages,
      }}
    >
      {data && children}
      {formStatus && (
        <Form
          checkDeptCodeRepeat={checkDeptCodeRepeat}
          getUnselectedEmployee={getUnselectedEmployee}
          employee={employee}
          onClose={onFormShow}
          onSubmit={onFormSubmit}
          defaultValues={selectedNode}
          isEditRootNode={isEditRootNode}
          getParentNode={getParentNode}
          isConfirmed={isConfirmed}
        />
      )}
    </ChartDataContext.Provider>
  );
};
